import React, { useState } from 'react';

interface Props {
  value: string;
  options: string[];
  onChange: (next: string) => void;
  // Returns the canonical value (e.g. case-folded against existing options).
  onAddOption: (value: string) => Promise<string> | string;
  placeholder?: string;
  allowClear?: boolean;
  className?: string;
}

const DropdownWithAddNew: React.FC<Props> = ({
  value,
  options,
  onChange,
  onAddOption,
  placeholder = 'Select…',
  allowClear = true,
  className,
}) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');

  const commitNew = async () => {
    const cleaned = draft.trim();
    if (!cleaned) {
      setAdding(false);
      return;
    }
    const canonical = (await onAddOption(cleaned)) || cleaned;
    onChange(canonical);
    setDraft('');
    setAdding(false);
  };

  if (adding) {
    return (
      <div className={`flex gap-1 ${className ?? ''}`}>
        <input
          autoFocus
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitNew();
            } else if (e.key === 'Escape') {
              setAdding(false);
              setDraft('');
            }
          }}
          placeholder="New value…"
          className="flex-1 border border-background-dark rounded px-2 py-1 text-sm"
        />
        <button
          type="button"
          onClick={commitNew}
          className="bg-primary text-white rounded px-2 py-1 text-xs"
        >
          Add
        </button>
        <button
          type="button"
          onClick={() => {
            setAdding(false);
            setDraft('');
          }}
          className="border border-background-dark rounded px-2 py-1 text-xs"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <select
      value={value}
      onChange={e => {
        if (e.target.value === '__add_new__') {
          setAdding(true);
          return;
        }
        onChange(e.target.value);
      }}
      className={`border border-background-dark rounded px-2 py-1 text-sm bg-white ${className ?? ''}`}
    >
      {allowClear && <option value="">{placeholder}</option>}
      {options.map(opt => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
      {value && !options.includes(value) && (
        <option key={value} value={value}>
          {value}
        </option>
      )}
      <option value="__add_new__">+ Add new…</option>
    </select>
  );
};

export default DropdownWithAddNew;
